"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import FilterCheckboxGroup from "@modules/common/components/filter-checkbox-group"

type VariantOption = {
  id: string
  title: string
  values: string[]
}

type VariantFiltersProps = {
  options: VariantOption[]
  "data-testid"?: string
}

const VariantFilters = ({
  options,
  "data-testid": dataTestId,
}: VariantFiltersProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  
  // Parse selected filters from URL
  const getSelectedValues = (optionTitle: string): string[] => {
    const param = searchParams.get(optionTitle.toLowerCase())
    return param ? param.split(",") : []
  }

  const handleFilterChange = useCallback(
    (optionTitle: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      const paramKey = optionTitle.toLowerCase()
      const currentValues = getSelectedValues(optionTitle)
      
      let newValues: string[]
      if (currentValues.includes(value)) {
        // Remove the value
        newValues = currentValues.filter(v => v !== value)
      } else {
        // Add the value
        newValues = [...currentValues, value]
      }
      
      if (newValues.length > 0) {
        params.set(paramKey, newValues.join(","))
      } else {
        params.delete(paramKey)
      }
      
      // Reset to page 1 when filters change
      params.delete("page")
      
      router.push(`${pathname}?${params.toString()}`)
    },
    [searchParams, pathname, router]
  )

  if (!options || options.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-6" data-testid={dataTestId}>
      {options.map((option) => (
        <FilterCheckboxGroup
          key={option.id}
          title={option.title}
          items={option.values.map(v => ({ value: v, label: v }))}
          selectedValues={getSelectedValues(option.title)}
          handleChange={(value) => handleFilterChange(option.title, value)}
        />
      ))}
    </div>
  )
}

export default VariantFilters

